import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Kilavuz from '../kilavuz/Kilavuz';
import SorunBildir from '../destek/SorunBildir';
import FihristKatmani from './FihristKatmani';
import Tur from '../kilavuz/Tur';
import { BOLUMLER, rotadanBolum, type Bolum, type TurAdimi } from '../kilavuz/icerik';
import { ROLE_LABEL } from '@artmozaik/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import AyarPaneli, { ayarDonus, useGenisEkran } from './AyarPaneli';
import Ikon from './Ikon';
import KomutArama from './KomutArama';
import { capayaGitVurgula } from './aramaYardimci';
import { ANA_MENU, AYARLAR_OGE, IKINCIL_MENU, altCubuk, etkinDal, gorunenMenu, yolEslesir, type GorunenOge, type RozetAd } from './menu';
import { altPayAyarla, domIsiEkle, sorunBildirGizle, useCekmeceIzleyici, useGeriKapat, useKaydirmaHatirla, useKlavyePayi, useTelefon } from './mobil';
import { kartTiklamasi, tablolariEtiketle } from './tabloKart';
import { BaglantiSeridi, KurulumKarti, YeniSurumCubugu } from '../pwa/Pwa';
import { usePwa } from '../pwa/durum';

/**
 * Ortak kabuk: koyu sol menü + açık içerik "kâğıdı" + üst şerit.
 *
 * Menü ağacı `ui/menu.ts`'tedir ve YETKİYE göre süzülür (API tarafında da kapalı;
 * ikisi aynı yetki listesinden beslenir). Ana öğe + girintili alt öğe düzeni:
 * aynı anda yalnız bir dalın kırılımı açıktır. Menü ikon şeridine daraltılabilir;
 * Ayarlar'a girince kendiliğinden daralır ve yanında ikinci düzey panel açılır.
 */
function bashaHarfler(ad: string): string {
  return ad.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const KISAYOL = MAC ? '⌘K' : 'Ctrl K';
const DAR_ANAHTARI = 'ccd-menu-dar';

type Rozetler = Partial<Record<RozetAd, number>>;

/**
 * Menü sayaçları: yalnız var olan uçlardan okunur, yetkisi olmayan role istek atılmaz,
 * 90 saniyede bir tazelenir, hata sessiz geçilir (sayaç görünmez, menü bozulmaz).
 */
function useRozetler(): Rozetler {
  const { can } = useAuth();
  const [r, setR] = useState<Rozetler>({});
  useEffect(() => {
    let canli = true;
    const yonetici = can('settings.write');
    const oku = async () => {
      if (document.hidden) return;
      const sifir = () => undefined;
      const [teklif, hatirlatma, cek, destek] = await Promise.all([
        // Tavanı aşan satırı olup yetkili onayı bekleyen teklifler (Teklifler ekranının özeti).
        can('quote.view') ? api<{ ozet: { onayBekleyen: number } }>('/api/teklif').then((x) => x.ozet.onayBekleyen).catch(sifir) : undefined,
        // Onay bekleyen borç hatırlatma iletileri (kuyruk).
        can('reminder.view') ? api<{ satirlar: unknown[] }>('/api/hatirlatma/kuyruk?durum=BEKLIYOR&limit=500').then((x) => x.satirlar.length).catch(sifir) : undefined,
        // Vadesi geçmiş, hâlâ bekleyen alınan ve verilen çek / senetler.
        can('cheque.view') ? api<{ ozet: { gecmisGirisAdet: number; gecmisCikisAdet: number }[] }>('/api/cek/ozet')
          .then((x) => x.ozet.reduce((t, s) => t + s.gecmisGirisAdet + s.gecmisCikisAdet, 0)).catch(sifir) : undefined,
        // Yönetici: açık talepler. Diğerleri: kendi talebine gelen, henüz görmediği yanıtlar.
        yonetici ? api<{ talepler: { durum: string }[] }>('/api/destek').then((x) => x.talepler.filter((t) => t.durum === 'ACIK').length).catch(sifir)
          : api<{ adet: number }>('/api/destek/bildirim').then((x) => x.adet).catch(sifir),
      ]);
      if (canli) setR({ teklif, hatirlatma, cek, destek });
    };
    void oku();
    const z = window.setInterval(() => void oku(), 90_000);
    return () => { canli = false; window.clearInterval(z); };
  }, [can]);
  return r;
}

/** Menüde yeri olmayan sayfaların dar ekrandaki üst şerit adı. */
const MENU_DISI_AD: Record<string, string> = { '/parola': 'Hesabım', '/telegram': "Telegram'ım", '/fihrist': 'Fihrist', '/kilavuz': 'Kullanım kılavuzu' };

const ROZET_ACIKLAMA: Record<RozetAd, string> = {
  teklif: 'onay bekleyen teklif', hatirlatma: 'onay bekleyen hatırlatma', cek: 'vadesi geçmiş çek / senet', destek: 'açık destek talebi ya da yeni yanıt',
};

function Rozet({ ad, sayi }: { ad?: RozetAd; sayi: Rozetler }) {
  const n = ad ? sayi[ad] : undefined;
  if (!ad || !n) return null;
  return <span className="ccd-rozet" title={`${n} ${ROZET_ACIKLAMA[ad]}`} aria-label={`${n} ${ROZET_ACIKLAMA[ad]}`}>{n > 99 ? '99+' : n}</span>;
}

export default function Shell() {
  const { me, logout, can } = useAuth();
  const konum = useLocation();
  const { pathname, search, hash } = konum;
  const navigate = useNavigate();
  const genis = useGenisEkran();
  const telefon = useTelefon();
  const pwa = usePwa();
  const rozet = useRozetler();

  /* ─── Telefon kabuğu: katmanlar, klavye payı, kaydırma konumu, tablo → kart ─── */
  useCekmeceIzleyici();
  useKlavyePayi();
  useKaydirmaHatirla();
  useEffect(() => {
    const birak = domIsiEkle(tablolariEtiketle);
    document.addEventListener('click', kartTiklamasi);
    return () => { birak(); document.removeEventListener('click', kartTiklamasi); };
  }, []);
  useEffect(() => { altPayAyarla(!genis, telefon); return () => altPayAyarla(false, false); }, [genis, telefon]);
  const altOgeler = useMemo(() => (me ? altCubuk(me.role, can) : []), [me, can]);

  const menu = useMemo(() => gorunenMenu(ANA_MENU, can), [can]);
  const ikincil = useMemo(() => gorunenMenu(IKINCIL_MENU, can), [can]);
  const ayarOge = useMemo(() => gorunenMenu([AYARLAR_OGE], can)[0] as GorunenOge | undefined, [can]);
  const tumu = useMemo(() => [...menu, ...ikincil, ...(ayarOge ? [ayarOge] : [])], [menu, ikincil, ayarOge]);
  const etkin = useMemo(() => etkinDal(tumu, pathname), [tumu, pathname]);
  const etkinOge = tumu.find((o) => o.id === etkin?.id);
  const etkinAlt = etkinOge?.altlar.find((a) => a.to === etkin?.alt);
  /** Sayfaların okuduğu bağlam (eski biçim korunur). */
  const aktif = etkinOge ? { to: etkinAlt?.to ?? etkinOge.hedef, label: etkinAlt?.label ?? etkinOge.label, cap: etkinAlt?.cap ?? etkinOge.cap } : undefined;

  /* ─── Menü durumu ──────────────────────────────────────────────────── */
  const ayarKip = pathname === '/ayarlar' || pathname.startsWith('/ayarlar/');
  const [dar, setDar] = useState(() => { try { return localStorage.getItem(DAR_ANAHTARI) === '1'; } catch { return false; } });
  const darDegis = () => setDar((d) => { try { localStorage.setItem(DAR_ANAHTARI, d ? '0' : '1'); } catch { /* özel pencere */ } return !d; });
  /** İkon şeridi: kullanıcı daralttıysa ya da Ayarlar paneli açıksa (yalnız geniş ekranda). */
  const serit = genis && (dar || ayarKip);
  const [cekmece, setCekmece] = useState(false);       // dar ekranda soldan açılan menü
  const [arama, setArama] = useState(false);
  const [ustMenu, setUstMenu] = useState(false);        // dar ekranda ⋯: yardım, fihrist, sunum, Paraşüt, çıkış
  /** "Bu sayfayı yenile": sayfa bileşeni yeniden kurulur, veriler baştan çekilir (tarayıcı yenilemesi değil; oturum ve adres aynı kalır). */
  const [tazele, setTazele] = useState(0);
  const [acikDal, setAcikDal] = useState<string | null>(etkin?.id ?? null);
  const [ucan, setUcan] = useState<{ id: string; ust: number } | null>(null);   // şeritte üstüne gelinen öğenin balonu
  const ucanZaman = useRef<number | null>(null);

  // Sayfa değişince: etkin dalın kırılımı açılır, çekmece ve balon kapanır.
  useEffect(() => { setAcikDal(etkin?.id ?? null); setCekmece(false); setUcan(null); setUstMenu(false); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // Ayarlar'a girmeden önceki sayfa hatırlanır: panel kapanınca oraya dönülür.
  useEffect(() => { if (!ayarKip) ayarDonus.yaz(pathname + search); }, [ayarKip, pathname, search]);
  // `#bölüm` ile gelindiyse (arama sonucu, eski bağlantı) öğeye inilir ve kısa süre vurgulanır. Fihrist kendi seçimini yapar.
  useEffect(() => {
    if (!hash || pathname === '/fihrist') return;
    return capayaGitVurgula(decodeURIComponent(hash.slice(1)));
  }, [konum.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const ayarKapat = useCallback(() => navigate(ayarDonus.oku()), [navigate]);

  useEffect(() => { sorunBildirGizle((cekmece || ustMenu) && !genis); return () => sorunBildirGizle(false); }, [cekmece, ustMenu, genis]);
  // Telefonun geri tuşu açık katmanı kapatır, sayfadan çıkarmaz.
  useGeriKapat(cekmece && !genis, () => setCekmece(false));
  useGeriKapat(arama, () => setArama(false));
  useGeriKapat(ustMenu && !genis, () => setUstMenu(false));

  /**
   * Dar ekranda üst şeridin solu: iç sayfada ‹ geri, menüdeki bir sayfada ☰ menü.
   * "İç sayfa": adres hiçbir menü öğesinin kendisi değil (ürün kartı, teklif, cari, ayar bölümü…).
   */
  const menuSayfasi = tumu.some((o) => (o.kendi && o.to === pathname) || o.altlar.some((a) => a.to === pathname)) || altOgeler.some((a) => a.to === pathname);
  const ustYol = pathname.split('/').slice(0, -1).join('/') || '/';
  const geriGit = () => {
    // Uygulama içinden gelindiyse tarayıcı geçmişi (liste kaldığı yerde açılır); doğrudan bağlantıyla gelindiyse üst sayfa.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1); else navigate(ustYol);
  };
  useEffect(() => {
    const tus = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') { e.preventDefault(); setArama((a) => !a); return; }
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (ustMenu) { setUstMenu(false); return; }
      if (cekmece) { setCekmece(false); return; }
      // Esc ile Ayarlar kapanır; açık bir pencere, çekmece ya da yazılan alan varsa ona karışılmaz.
      const h = e.target as HTMLElement | null;
      if (ayarKip && genis && !arama && !document.querySelector('.ccd-drawer-arka, [role="dialog"]') && !(h && /^(INPUT|TEXTAREA|SELECT)$/.test(h.tagName))) ayarKapat();
    };
    window.addEventListener('keydown', tus);
    return () => window.removeEventListener('keydown', tus);
  }, [ayarKip, genis, arama, cekmece, ustMenu, ayarKapat]);

  const ucanAc = (id: string, el: HTMLElement) => {
    if (!serit) return;
    if (ucanZaman.current) window.clearTimeout(ucanZaman.current);
    setUcan({ id, ust: el.getBoundingClientRect().top });
  };
  const ucanKapat = () => {
    if (ucanZaman.current) window.clearTimeout(ucanZaman.current);
    ucanZaman.current = window.setTimeout(() => setUcan(null), 140);
  };
  const ucanTut = () => { if (ucanZaman.current) window.clearTimeout(ucanZaman.current); };

  /** Menüde ok tuşları: ↑/↓ satırlar arasında, → kırılımı açar, ← kapatır. */
  const menuTus = (e: React.KeyboardEvent<HTMLElement>) => {
    const h = e.target as HTMLElement;
    const dal = h.closest<HTMLElement>('[data-dal]')?.dataset.dal;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const satirlar = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('a.ccd-navitem, a.ccd-altoge'));
      const i = satirlar.indexOf(h.closest('a') as HTMLElement);
      if (i < 0) return;
      e.preventDefault();
      satirlar[(i + (e.key === 'ArrowDown' ? 1 : -1) + satirlar.length) % satirlar.length]?.focus();
    } else if (e.key === 'ArrowRight' && dal) { e.preventDefault(); setAcikDal(dal); }
    else if (e.key === 'ArrowLeft' && dal) { e.preventDefault(); setAcikDal(null); h.closest<HTMLElement>('[data-dal]')?.querySelector<HTMLElement>('a.ccd-navitem')?.focus(); }
  };

  const oge = (o: GorunenOge, kucuk?: boolean) => {
    const dalEtkin = etkin?.id === o.id;
    const kendiEtkin = dalEtkin && !etkin?.alt;
    const acik = !serit && acikDal === o.id && o.altlar.length > 0;
    return (
      <div key={o.id} className={`ccd-dal${acik ? ' ccd-dal-acik' : ''}${kucuk ? ' ccd-dal-kucuk' : ''}`} data-dal={o.altlar.length ? o.id : undefined}
           onMouseEnter={(e) => ucanAc(o.id, e.currentTarget)} onMouseLeave={ucanKapat}>
        <div className="ccd-dal-satir">
          <Link to={o.hedef} className={`ccd-navitem${kendiEtkin ? ' ccd-is-active' : ''}${dalEtkin ? ' ccd-dal-etkin' : ''}`}
                aria-current={kendiEtkin ? 'page' : undefined} aria-label={serit ? o.label : undefined}
                onClick={() => setAcikDal(o.id)} onFocus={(e) => ucanAc(o.id, e.currentTarget.parentElement!.parentElement!)} onBlur={ucanKapat}>
            <Ikon ad={o.ikon} />
            <span className="ccd-nav-ad">{o.label}</span>
            <Rozet ad={o.rozet} sayi={rozet} />
          </Link>
          {o.altlar.length > 0 && !serit && (
            <button type="button" className="ccd-dal-ok" aria-expanded={acik} aria-label={`${o.label} alt sayfalarını ${acik ? 'kapat' : 'aç'}`}
                    onClick={() => setAcikDal(acik ? null : o.id)}><Ikon ad="asagi" boy={16} /></button>
          )}
        </div>
        {acik && (
          <div className="ccd-dal-alt" role="group" aria-label={o.label}>
            {o.altlar.map((a) => (
              <Link key={a.to} to={a.to} className={`ccd-altoge${etkin?.id === o.id && etkin.alt === a.to ? ' ccd-is-active' : ''}`}
                    aria-current={etkin?.id === o.id && etkin.alt === a.to ? 'page' : undefined}>
                <span className="ccd-nav-ad">{a.label}</span>
                <Rozet ad={a.rozet} sayi={rozet} />
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };
  const ucanOge = ucan && serit ? tumu.find((o) => o.id === ucan.id) : undefined;

  /* Kılavuz (ⓘ) ve ekran turu. Tur adımları o anki ekranın bölümünden gelir. */
  const [kilavuzAcik, setKilavuzAcik] = useState(false);
  const [turAdimlari, setTurAdimlari] = useState<TurAdimi[] | null>(null);
  const [turTeklifi, setTurTeklifi] = useState(false);
  const bolum = rotadanBolum(pathname, can);
  useGeriKapat(kilavuzAcik, () => setKilavuzAcik(false));

  // İlk ziyarette bir kez "adım adım göstereyim mi?" — cevabı tarayıcıda kalır.
  useEffect(() => {
    setTurTeklifi(false);
    if (!bolum?.turVar || !bolum.adimlar?.length) return;
    try {
      if (!localStorage.getItem(`am_tur_${bolum.id}`)) {
        const t = setTimeout(() => setTurTeklifi(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* gizli pencere: teklif etme */ }
  }, [bolum?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Bir bölümün turunu başlat — rolün görmediği adımlar atlanır. */
  const bolumTuruBaslat = (b: Bolum) => {
    const adimlar = (b.adimlar ?? []).filter((a) => !a.cap || can(a.cap));
    if (adimlar.length === 0) return;
    try { localStorage.setItem(`am_tur_${b.id}`, '1'); } catch { /* yok say */ }
    setTurTeklifi(false); setKilavuzAcik(false);
    setTurAdimlari(adimlar);
  };
  const turuBaslat = () => { if (bolum) bolumTuruBaslat(bolum); };

  // Derin bağlantı: /?tur=akis → uçtan uca tur. Gösterge düğmesi ve README bunu kullanır.
  useEffect(() => {
    const id = new URLSearchParams(search).get('tur');
    if (!id) return;
    const b = BOLUMLER.find((x) => x.id === id);
    navigate(pathname, { replace: true });
    if (b) setTimeout(() => bolumTuruBaslat(b), 400);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  const turuGecistir = () => {
    try { if (bolum) localStorage.setItem(`am_tur_${bolum.id}`, '1'); } catch { /* yok say */ }
    setTurTeklifi(false);
  };

  return (
    <div className="ccd">
      <div className={`ccd-app ccd-kabuk${serit ? ' ccd-kabuk-serit' : ''}${ayarKip && genis ? ' ccd-kabuk-altpanel' : ''}${cekmece ? ' ccd-kabuk-cekmece' : ''}`}>
        {cekmece && <div className="ccd-cekmece-perde" onClick={() => setCekmece(false)} aria-hidden="true" />}
        <aside className="ccd-sidebar" aria-label="Ana menü">
          <div className="ccd-sidebar-brand">
            <Link to="/" className="ccd-marka" aria-label="Art Mozaik Panel — Genel Özet">
              <span className="ccd-marka-kare" aria-hidden="true">AM</span>
              <span className="ccd-marka-ad">Art Mozaik <span className="ccd-marka-vurgu">Panel</span></span>
            </Link>
            {genis && !ayarKip && (
              <button type="button" className="ccd-menu-dugme ccd-menu-daralt" onClick={darDegis} aria-pressed={dar}
                      title={dar ? 'Menüyü genişlet' : 'Menüyü daralt (yalnız ikonlar)'} aria-label={dar ? 'Menüyü genişlet' : 'Menüyü daralt'}>
                <Ikon ad={dar ? 'genislet' : 'daralt'} boy={18} />
              </button>
            )}
            {!genis && (
              <button type="button" className="ccd-menu-dugme" onClick={() => setCekmece(false)} aria-label="Menüyü kapat"><Ikon ad="kapat" boy={18} /></button>
            )}
          </div>

          <button type="button" className="ccd-menu-ara" onClick={() => { setCekmece(false); setArama(true); }} data-tur="arama"
                  title={`Sayfa, ayar, ürün, cari, teklif ara (${KISAYOL})`} aria-label={`Ara (${KISAYOL})`}>
            <Ikon ad="arama" boy={18} />
            <span className="ccd-nav-ad">Ara</span>
            <kbd className="ccd-menu-kisayol">{KISAYOL}</kbd>
          </button>

          <nav className="ccd-sidebar-group" aria-label="Sayfalar" onKeyDown={menuTus}>
            {menu.map((o) => oge(o))}
          </nav>
          <div className="ccd-spacer" />
          <nav className="ccd-sidebar-group ccd-sidebar-alt" aria-label="Sistem">
            {/* Muhasebe programına doğrudan giriş: yeni sekmede açılır, kullanıcı orada kendi hesabıyla girer. */}
            {me?.muhasebeAdres && (
              <div className="ccd-dal ccd-dal-kucuk">
                <div className="ccd-dal-satir">
                  <a href={me.muhasebeAdres} target="_blank" rel="noopener noreferrer" className="ccd-navitem"
                     title="Paraşüt'ü yeni sekmede aç" aria-label={serit ? "Paraşüt'ü aç" : undefined}>
                    <Ikon ad="banka" />
                    <span className="ccd-nav-ad">Paraşüt</span>
                    <span className="ccd-nav-dis" aria-hidden="true">↗</span>
                  </a>
                </div>
              </div>
            )}
            {ikincil.map((o) => oge(o, true))}
            {ayarOge && oge(ayarOge)}
          </nav>
          <div className="ccd-kullanici">
            {/* Kullanıcı kartı parola ekranına gider. */}
            <Link to="/parola" className="ccd-kullanici-kart" title="Parola değiştir">
              <span className="ccd-avatar">{me ? bashaHarfler(me.name) : '—'}</span>
              <span className="ccd-kullanici-metin">
                <span className="ccd-kullanici-ad ccd-truncate">{me?.name}</span>
                <span className="ccd-kullanici-rol ccd-truncate">{me ? ROLE_LABEL[me.role] : ''}</span>
              </span>
            </Link>
            <Link to="/telegram" className="ccd-menu-dugme" title="Telegram'ım: bağlantı ve bildirim tercihleri" aria-label="Telegram'ım"><Ikon ad="ucak" boy={18} /></Link>
            <button type="button" className="ccd-menu-dugme" onClick={() => void logout()} title="Çıkış" aria-label="Çıkış"><Ikon ad="cikis" boy={18} /></button>
          </div>
        </aside>

        {/* Şeritte üstüne gelinen öğenin etiketi ve alt sayfaları. */}
        {ucanOge && ucan && (
          <div className="ccd-ucan" style={{ top: Math.max(8, Math.min(ucan.ust, window.innerHeight - 60 - ucanOge.altlar.length * 34)) }}
               onMouseEnter={ucanTut} onMouseLeave={ucanKapat} role="group" aria-label={ucanOge.label}>
            <Link to={ucanOge.hedef} className="ccd-ucan-baslik" onClick={() => setUcan(null)}>{ucanOge.label}<Rozet ad={ucanOge.rozet} sayi={rozet} /></Link>
            {ucanOge.altlar.map((a) => (
              <Link key={a.to} to={a.to} className={`ccd-altoge${etkin?.alt === a.to ? ' ccd-is-active' : ''}`} onClick={() => setUcan(null)}>
                <span className="ccd-nav-ad">{a.label}</span><Rozet ad={a.rozet} sayi={rozet} />
              </Link>
            ))}
          </div>
        )}

        {ayarKip && genis && <AyarPaneli />}

        <div className="ccd-shell">
          {/* Mock kipte KALICI şerit: veriyi gerçek sanmayı engeller. */}
          {me?.parasutKip === 'mock' && (
            <div className="ccd-mock-serit" role="status" title="Paraşüt'ten gelen faturalar örnektir, gerçek hesaptan gelmiyor. Stok ve tahsilat kararlarını bu veriye göre vermeyin.">
              <strong>MOCK VERİ — canlı değil.</strong>{' '}
              <span className="ccd-mock-uzun">Paraşüt'ten gelen faturalar
              örnektir, gerçek hesaptan gelmiyor. Stok ve tahsilat kararlarını bu
              veriye göre vermeyin.</span>
              <span className="ccd-mock-kisa">Faturalar örnektir; karar vermeyin.</span>
            </div>
          )}
          <BaglantiSeridi />
          {!genis ? (
            /* Dar ekran: solda ‹ geri ya da ☰, ortada sayfa adı, sağda arama ve ⋯. */
            <div className="ccd-topbar ccd-topbar-dar">
              {menuSayfasi || !etkinOge ? (
                <button type="button" className="ccd-ust-btn ccd-ust-menu" onClick={() => setCekmece(true)} aria-label="Menüyü aç" aria-expanded={cekmece}><Ikon ad="menu" boy={22} /></button>
              ) : (
                <button type="button" className="ccd-ust-btn ccd-ust-geri" onClick={geriGit} aria-label="Geri"><Ikon ad="geri" boy={22} /></button>
              )}
              <div className="ccd-topbar-ad ccd-truncate" aria-label="Bulunduğunuz yer">{etkinAlt?.label ?? etkinOge?.label ?? MENU_DISI_AD[pathname] ?? 'Art Mozaik'}</div>
              <button type="button" className="ccd-ust-btn ccd-ust-ara" onClick={() => setArama(true)} aria-label="Ara"><Ikon ad="arama" boy={22} /></button>
              <button type="button" className="ccd-ust-btn ccd-ust-diger" onClick={() => setUstMenu(true)} aria-label="Diğer: yardım, fihrist, çıkış" aria-haspopup="menu" aria-expanded={ustMenu}><Ikon ad="uc" boy={22} /></button>
            </div>
          ) : (
          <div className="ccd-topbar">
            <div className="ccd-topbar-sol">
              <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-icon ccd-ust-menu" onClick={() => setCekmece(true)} aria-label="Menüyü aç" aria-expanded={cekmece}>
                <Ikon ad="menu" />
              </button>
              <div className="ccd-topbar-yol ccd-truncate" aria-label="Bulunduğunuz yer">
                {etkinOge ? (
                  etkinAlt ? <><span className="ccd-topbar-ust">{etkinOge.label}</span><Ikon ad="ok" boy={14} /><b>{etkinAlt.label}</b></> : <b>{etkinOge.label}</b>
                ) : null}
              </div>
            </div>
            <div className="ccd-row ccd-topbar-sag">
              <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-icon ccd-ust-ara" onClick={() => setArama(true)} title={`Ara (${KISAYOL})`} aria-label="Ara">
                <Ikon ad="arama" />
              </button>
              <button className="ccd-btn ccd-btn-ghost ccd-btn-icon" onClick={() => setKilavuzAcik(true)}
                      title="Kullanım kılavuzu — bu ekranın bölümünde açılır" aria-label="Kullanım kılavuzu"
                      data-tur="kilavuz"><Ikon ad="bilgi" /></button>
              <Link className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-ust-dugme" to="/fihrist" aria-label="Fihrist"
                    title="Fihrist — her rakam ve kavram: ne nedir, nasıl hesaplanır. Bir rakama sağ tıklayıp “Bu nedir?” de diyebilirsiniz."><Ikon ad="kitap" boy={18} /><span className="ccd-ust-yazi">Fihrist</span></Link>
              {can('settings.write') && (
                <Link className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-ust-dugme" to="/sunum" aria-label="Sunum"
                      title="Ne konuştuk, ne yaptık, ne bekliyor — yönetici sunumu"><Ikon ad="sunum" boy={18} /><span className="ccd-ust-yazi">Sunum</span></Link>
              )}
              {/* Paraşüt kipi gerçek duruma bağlı — sabit metin değil. */}
              {me?.muhasebeAdres ? (
                <a className="ccd-badge ccd-ust-rozet ccd-badge-success ccd-ust-rozet-bag" href={me.muhasebeAdres} target="_blank" rel="noopener noreferrer"
                   title="Paraşüt'ü yeni sekmede aç (kendi Paraşüt kullanıcınızla giriş yaparsınız)">
                  <span className="ccd-badge-dot" />
                  <span className="ccd-ust-rozet-yazi">Paraşüt bağlı</span>
                  <span aria-hidden="true">↗</span>
                </a>
              ) : (
                <span
                  className={`ccd-badge ccd-ust-rozet${me?.parasutKip === 'real' ? ' ccd-badge-success' : ''}`}
                  title={
                    me?.parasutKip === 'real' ? 'Gerçek Paraşüt hesabına bağlı'
                    : me?.parasutKip === 'mock' ? 'Mock kip — veriler örnek'
                    : 'Paraşüt bilgileri henüz gelmedi'
                  }
                >
                  <span className="ccd-badge-dot" />
                  <span className="ccd-ust-rozet-yazi">{me?.parasutKip === 'real' ? 'Paraşüt bağlı'
                    : me?.parasutKip === 'mock' ? 'Paraşüt · MOCK'
                    : 'Paraşüt bağlanmadı'}</span>
                </span>
              )}
              <button className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-ust-dugme" onClick={() => void logout()} aria-label="Çıkış">
                <Ikon ad="cikis" boy={18} /><span className="ccd-ust-yazi">Çıkış</span>
              </button>
            </div>
          </div>
          )}

          <main className="ccd-main">
            <div className="ccd-container ccd-stack" key={tazele}>
              <Outlet context={{ aktif }} />
            </div>
          </main>
        </div>

        {/* Telefon alt gezinme çubuğu: role göre dört ekran + Menü. */}
        {!genis && (
          <nav className="ccd-altbar" aria-label="Hızlı gezinme">
            {altOgeler.map((a) => {
              const etkinMi = yolEslesir(pathname, a.to, a.tam);
              return (
                <Link key={a.to + a.label} to={a.to} className={`ccd-altbar-oge${etkinMi ? ' ccd-is-active' : ''}`} aria-current={etkinMi ? 'page' : undefined}>
                  <span className="ccd-altbar-ikon"><Ikon ad={a.ikon} boy={22} /><Rozet ad={a.rozet} sayi={rozet} /></span>
                  <span className="ccd-altbar-ad">{a.label}</span>
                </Link>
              );
            })}
            <button type="button" className={`ccd-altbar-oge ccd-altbar-menu${cekmece ? ' ccd-is-active' : ''}`} onClick={() => setCekmece((c) => !c)} aria-expanded={cekmece} aria-label="Menü: bütün sayfalar">
              <span className="ccd-altbar-ikon"><Ikon ad="menu" boy={22} /><Rozet ad={rozet.destek ? 'destek' : undefined} sayi={rozet} /></span>
              <span className="ccd-altbar-ad">Menü</span>
            </button>
          </nav>
        )}
      </div>

      {/* Dar ekranda ⋯ menüsü: üst şeritteki düğmelerin hiçbiri kaybolmaz, burada toplanır. */}
      {ustMenu && !genis && (
        <div className="ccd-ustmenu-perde" onClick={() => setUstMenu(false)} role="presentation">
          <div className="ccd-ustmenu" role="menu" aria-label="Diğer" onClick={(e) => e.stopPropagation()}>
            <div className="ccd-ustmenu-kim">
              <span className="ccd-avatar">{me ? bashaHarfler(me.name) : '—'}</span>
              <span className="ccd-kullanici-metin"><b className="ccd-truncate">{me?.name}</b><span className="ccd-truncate">{me ? ROLE_LABEL[me.role] : ''}</span></span>
              <button type="button" className="ccd-ust-btn" onClick={() => setUstMenu(false)} aria-label="Kapat"><Ikon ad="kapat" boy={20} /></button>
            </div>
            <button type="button" role="menuitem" className="ccd-ustmenu-oge" onClick={() => { setUstMenu(false); setTazele((t) => t + 1); }}><Ikon ad="yenile" /><span>Bu sayfayı yenile <small>rakamları yeniden çek</small></span></button>
            <button type="button" role="menuitem" className="ccd-ustmenu-oge" onClick={() => { setUstMenu(false); setKilavuzAcik(true); }}><Ikon ad="bilgi" /><span>Kullanım kılavuzu</span></button>
            <Link role="menuitem" className="ccd-ustmenu-oge" to="/fihrist"><Ikon ad="kitap" /><span>Fihrist <small>her rakam ve kavram: ne nedir</small></span></Link>
            {can('settings.write') && <Link role="menuitem" className="ccd-ustmenu-oge" to="/sunum"><Ikon ad="sunum" /><span>Sunum</span></Link>}
            {me?.muhasebeAdres ? (
              <a role="menuitem" className="ccd-ustmenu-oge" href={me.muhasebeAdres} target="_blank" rel="noopener noreferrer"><Ikon ad="banka" /><span>Paraşüt'ü aç <small>bağlı · yeni sekme ↗</small></span></a>
            ) : (
              <div className="ccd-ustmenu-oge ccd-ustmenu-bilgi"><Ikon ad="banka" /><span>Paraşüt <small>{me?.parasutKip === 'real' ? 'bağlı' : me?.parasutKip === 'mock' ? 'MOCK kip — veriler örnek' : 'henüz bağlanmadı'}</small></span></div>
            )}
            <Link role="menuitem" className="ccd-ustmenu-oge" to="/telegram"><Ikon ad="ucak" /><span>Telegram'ım <small>bağlantı ve bildirimler</small></span></Link>
            <Link role="menuitem" className="ccd-ustmenu-oge" to="/parola"><Ikon ad="kilit" /><span>Parola</span></Link>
            {!pwa.kurulu && <Link role="menuitem" className="ccd-ustmenu-oge" to="/parola#uygulama"><Ikon ad="indir" /><span>Uygulamayı kur <small>ana ekrana ekle</small></span></Link>}
            <button type="button" role="menuitem" className="ccd-ustmenu-oge ccd-ustmenu-cikis" onClick={() => void logout()}><Ikon ad="cikis" /><span>Çıkış</span></button>
          </div>
        </div>
      )}

      {arama && <KomutArama kapat={() => setArama(false)} />}
      {/* Sorun Bildir — tek satır; kaldırmak için bu satırı silin ya da ayarda aktif:0 yapın. */}
      <SorunBildir />
      {/* «?» balonu ve sağ tuş "Bu nedir?" menüsü — tek satır. */}
      <FihristKatmani />
      {kilavuzAcik && (
        <Kilavuz acilis={bolum?.id} kapat={() => setKilavuzAcik(false)}
                 turBaslat={bolumTuruBaslat} />
      )}
      {turAdimlari && <Tur adimlar={turAdimlari} bitir={() => setTurAdimlari(null)} />}
      <YeniSurumCubugu />
      <KurulumKarti engel={pwa.yeniSurum || turTeklifi || Boolean(turAdimlari) || kilavuzAcik || arama || cekmece || ustMenu} />
      {turTeklifi && !turAdimlari && !kilavuzAcik && (
        <div className="ccd-tur-teklif" role="status">
          <div style={{ fontWeight: 600 }}>Bu ekranda ilk kez buradasınız</div>
          <div className="ccd-meta" style={{ margin: '4px 0 10px' }}>
            Düğmeleri sırayla göstereyim mi? Bir dakika sürer; sonra ⓘ'den hep ulaşırsınız.
          </div>
          <div className="ccd-row">
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={turuBaslat}>Adım adım göster</button>
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={turuGecistir}>Gerek yok</button>
          </div>
        </div>
      )}
    </div>
  );
}
